import { BedarfStatus } from '../../../types/bedarf';

// SVG Icon Components
export const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

export const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

export const TrophyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
  </svg>
);

export const EnvelopeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

export const FlagIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
  </svg>
);

export const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

export const XCircleIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export type BedarfModalType = 'kennenlernen' | 'priority';

// Configuration for status-specific actions
export interface BedarfActionConfig {
  key: string;
  label: string;
  loadingLabel: string;
  color: string;
  hoverColor: string;
  action: 'statusChange' | 'modal' | 'navigate' | 'mailto';
  newStatus?: BedarfStatus;
  modalType?: BedarfModalType;
  navigateTo?: string;
  icon: React.FC;
  tooltip: string;
  group: 'primary' | 'secondary' | 'danger';
  disabled?: boolean; // For temporarily disabled actions
}

// Status-specific primary actions
export const STATUS_ACTIONS: Partial<Record<BedarfStatus, BedarfActionConfig[]>> = {
  [BedarfStatus.NOCH_NICHT_RELEVANT]: [
    {
      key: 'weiter-zu-anfrage',
      label: 'Weiter zu Anfrage',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-purple-300 text-purple-700 bg-purple-50',
      hoverColor: 'hover:bg-purple-100',
      action: 'statusChange',
      newStatus: BedarfStatus.ANFRAGE,
      icon: ArrowRightIcon,
      tooltip: 'Markiert den Bedarf als Anfrage',
      group: 'primary',
    },
  ],

  [BedarfStatus.ANFRAGE]: [
    {
      key: 'freigeben',
      label: 'Freigeben (Offen)',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-blue-300 text-blue-700 bg-blue-50',
      hoverColor: 'hover:bg-blue-100',
      action: 'statusChange',
      newStatus: BedarfStatus.OFFEN,
      icon: CheckIcon,
      tooltip: 'Gibt den Bedarf frei',
      group: 'primary',
    },
  ],

  [BedarfStatus.OFFEN]: [
    {
      key: 'kennenlernen',
      label: 'Kennenlernen planen',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-green-300 text-green-700 bg-green-50',
      hoverColor: 'hover:bg-green-100',
      action: 'modal',
      modalType: 'kennenlernen',
      icon: CalendarIcon,
      tooltip: 'Bald verfügbar',
      group: 'primary',
      disabled: true,
    },
    {
      key: 'weiter-zu-onboarding',
      label: 'Weiter zu Onboarding',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-teal-300 text-teal-700 bg-teal-50',
      hoverColor: 'hover:bg-teal-100',
      action: 'statusChange',
      newStatus: BedarfStatus.ONBOARDING,
      icon: ArrowRightIcon,
      tooltip: 'Startet das Onboarding für diesen Bedarf',
      group: 'primary',
    },
  ],

  [BedarfStatus.KENNENLERNGESPRAECH]: [
    {
      key: 'kennenlernen',
      label: 'Kennenlernen planen',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-green-300 text-green-700 bg-green-50',
      hoverColor: 'hover:bg-green-100',
      action: 'modal',
      modalType: 'kennenlernen',
      icon: CalendarIcon,
      tooltip: 'Bald verfügbar',
      group: 'primary',
      disabled: true,
    },
    {
      key: 'weiter-zu-onboarding',
      label: 'Weiter zu Onboarding',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-teal-300 text-teal-700 bg-teal-50',
      hoverColor: 'hover:bg-teal-100',
      action: 'statusChange',
      newStatus: BedarfStatus.ONBOARDING,
      icon: ArrowRightIcon,
      tooltip: 'Startet das Onboarding für diesen Bedarf',
      group: 'primary',
    },
  ],

  [BedarfStatus.TERMINIERT]: [
    {
      key: 'kennenlernen',
      label: 'Kennenlernen planen',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-green-300 text-green-700 bg-green-50',
      hoverColor: 'hover:bg-green-100',
      action: 'modal',
      modalType: 'kennenlernen',
      icon: CalendarIcon,
      tooltip: 'Bald verfügbar',
      group: 'primary',
      disabled: true,
    },
    {
      // The regular next step after a successful Kennenlerngespräch. The
      // shortcut straight to Onboarding stays below it, as it does in OFFEN and
      // KENNENLERNGESPRAECH.
      key: 'weiter-zu-kostentraeger-go',
      label: 'Weiter zu Kostenträger Go',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-indigo-300 text-indigo-700 bg-indigo-50',
      hoverColor: 'hover:bg-indigo-100',
      action: 'statusChange',
      newStatus: BedarfStatus.KOSTENTRAEGER_GO,
      icon: ArrowRightIcon,
      tooltip: 'Eltern und Bewerber sind sich einig — die Zusage des Kostenträgers fehlt noch',
      group: 'primary',
    },
    {
      key: 'weiter-zu-onboarding',
      label: 'Weiter zu Onboarding',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-teal-300 text-teal-700 bg-teal-50',
      hoverColor: 'hover:bg-teal-100',
      action: 'statusChange',
      newStatus: BedarfStatus.ONBOARDING,
      icon: ArrowRightIcon,
      tooltip: 'Startet das Onboarding für diesen Bedarf',
      group: 'primary',
    },
  ],

  [BedarfStatus.KOSTENTRAEGER_GO]: [
    {
      key: 'kennenlernen',
      label: 'Kennenlernen planen',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-green-300 text-green-700 bg-green-50',
      hoverColor: 'hover:bg-green-100',
      action: 'modal',
      modalType: 'kennenlernen',
      icon: CalendarIcon,
      tooltip: 'Bald verfügbar',
      group: 'primary',
      disabled: true,
    },
    {
      key: 'weiter-zu-onboarding',
      label: 'Weiter zu Onboarding',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-teal-300 text-teal-700 bg-teal-50',
      hoverColor: 'hover:bg-teal-100',
      action: 'statusChange',
      newStatus: BedarfStatus.ONBOARDING,
      icon: ArrowRightIcon,
      tooltip: 'Startet das Onboarding für diesen Bedarf',
      group: 'primary',
    },
  ],

  [BedarfStatus.ONBOARDING]: [
    {
      key: 'gewonnen',
      label: 'Als Gewonnen markieren',
      loadingLabel: 'Wird verarbeitet...',
      color: 'border-emerald-300 text-emerald-700 bg-emerald-50',
      hoverColor: 'hover:bg-emerald-100',
      action: 'statusChange',
      newStatus: BedarfStatus.GEWONNEN,
      icon: TrophyIcon,
      tooltip: 'Markiert den Bedarf als gewonnen (fragt nach, wenn Onboarding-Schritte offen sind)',
      group: 'primary',
    },
  ],

  // Terminal states have no primary actions
  [BedarfStatus.GEWONNEN]: [],
  [BedarfStatus.VERLOREN]: [],
};

// Secondary actions available for all statuses
export const SECONDARY_ACTIONS: BedarfActionConfig[] = [
  {
    key: 'edit',
    label: 'Bearbeiten',
    loadingLabel: 'Bearbeiten',
    color: 'text-gray-700',
    hoverColor: 'hover:bg-gray-100',
    action: 'navigate',
    navigateTo: '/bedarfe/:id/edit',
    icon: PencilIcon,
    tooltip: 'Bedarf bearbeiten',
    group: 'secondary',
  },
  {
    key: 'priority',
    label: 'Prioritaet aendern',
    loadingLabel: 'Prioritaet aendern',
    color: 'text-gray-700',
    hoverColor: 'hover:bg-gray-100',
    action: 'modal',
    modalType: 'priority',
    icon: FlagIcon,
    tooltip: 'Aendert die Prioritaet des Bedarfs',
    group: 'secondary',
  },
];

// Email action - dynamically added when parentEmail exists
export const EMAIL_PARENT_ACTION: BedarfActionConfig = {
  key: 'email-parent',
  label: 'E-Mail an Eltern',
  loadingLabel: 'E-Mail an Eltern',
  color: 'text-gray-700',
  hoverColor: 'hover:bg-gray-100',
  action: 'mailto',
  icon: EnvelopeIcon,
  tooltip: 'Oeffnet E-Mail-Client mit der Eltern-Adresse',
  group: 'secondary',
};

// Danger actions - available for non-terminal statuses
export const DANGER_ACTIONS: BedarfActionConfig[] = [
  {
    key: 'verloren',
    label: 'Als Verloren markieren',
    loadingLabel: 'Wird verarbeitet...',
    color: 'text-red-700',
    hoverColor: 'hover:bg-red-50',
    action: 'statusChange',
    newStatus: BedarfStatus.VERLOREN,
    icon: XCircleIcon,
    tooltip: 'Markiert den Bedarf als verloren',
    group: 'danger',
  },
];

// Terminal statuses where danger actions are not available
export const TERMINAL_STATUSES: BedarfStatus[] = [
  BedarfStatus.GEWONNEN,
  BedarfStatus.VERLOREN,
];

// Helper to check if a status is terminal
export function isTerminalStatus(status: BedarfStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}
